/**
 * Oscillator Explain modal (spec §11.2).
 *
 * Shows the per-input attribution for a given reading — raw value, z-score,
 * weight, contribution — along with the composite math and overlay trace.
 */

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { getOscillatorExplain } from '../api/client';
import { canonicalZone, zoneLabel, zoneTokens } from '../lib/oscillatorZone';

const INPUT_LABELS = {
  ad_line: 'Advance/Decline line',
  up_down_vol: 'Up/Down volume ratio',
  new_highs_lows: 'New highs − lows',
  pct_above_20sma: '% above 20-day SMA',
  spx_5d_roc: 'SPX 5-day ROC',
};

function fmt(v, digits = 4) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}`;
}

export default function OscillatorExplainModal({ date, onClose }) {
  const [state, setState] = useState({ status: 'loading', payload: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getOscillatorExplain(date);
        if (cancelled) return;
        if (!resp.available) {
          setState({ status: 'empty', payload: null, error: null });
        } else {
          setState({ status: 'ready', payload: resp.explain, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', payload: null, error: err });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const payload = state.payload;
  const zone = payload
    ? canonicalZone(payload.zone, payload.oscillator_value)
    : 'neutral';
  const tokens = zoneTokens(zone);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-slate-800">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Explain reading</p>
            <h2 className="text-xl font-semibold text-slate-100 mt-1">
              S&P Breadth Oscillator
            </h2>
            {payload && (
              <p className="text-sm text-slate-400 mt-1">
                {payload.trade_date} ·{' '}
                <span className={`font-mono font-bold ${tokens.textClass}`}>
                  {fmt(payload.oscillator_value, 2)}
                </span>{' '}
                <span className={`uppercase text-xs tracking-wide ${tokens.textClass}`}>
                  {zoneLabel(zone)}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {state.status === 'loading' && (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        )}

        {state.status === 'error' && (
          <div className="p-6 text-sm text-red-400">
            Failed to load: {state.error?.message || 'unknown error'}
          </div>
        )}

        {state.status === 'empty' && (
          <div className="p-6 text-sm text-slate-500">
            No reading available for {date || 'this date'}.
          </div>
        )}

        {state.status === 'ready' && payload && (
          <div className="p-6 space-y-6">
            {/* Input contributions */}
            <section>
              <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">
                Input contributions
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-slate-500 border-b border-slate-800">
                      <th className="text-left py-2 pr-4">Input</th>
                      <th className="text-right py-2 px-2 font-mono">Raw</th>
                      <th className="text-right py-2 px-2 font-mono">z-score</th>
                      <th className="text-right py-2 px-2 font-mono">Weight</th>
                      <th className="text-right py-2 pl-2 font-mono">Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(payload.inputs || {}).map(([key, row]) => (
                      <tr key={key} className="border-b border-slate-800/50 last:border-0">
                        <td className="py-2 pr-4 text-slate-300">
                          {INPUT_LABELS[key] || key}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-400">
                          {fmt(row.raw_value, 4)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-300">
                          {fmt(row.z_score, 2)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-slate-400">
                          {fmt(row.weight, 2)}
                        </td>
                        <td className={`py-2 pl-2 text-right font-mono font-semibold ${Number(row.contribution) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {fmt(row.contribution, 3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Composite math */}
            {payload.composite && (
              <section>
                <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">
                  Composite
                </h3>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <dt className="text-xs text-slate-500 uppercase">Raw sum</dt>
                    <dd className="font-mono text-slate-200 mt-0.5">
                      {fmt(payload.composite.raw_sum, 3)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 uppercase">Scale</dt>
                    <dd className="font-mono text-slate-200 mt-0.5">
                      {payload.composite.scale}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 uppercase">Clip</dt>
                    <dd className="font-mono text-slate-200 mt-0.5">
                      ±{payload.composite.clip} · {payload.composite.clipped ? 'clipped' : 'not clipped'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500 uppercase">Final</dt>
                    <dd className={`font-mono font-semibold mt-0.5 ${tokens.textClass}`}>
                      {fmt(payload.composite.final_value, 3)}
                    </dd>
                  </div>
                </dl>
              </section>
            )}

            {/* Overlay trace */}
            {payload.overlay_trace && (
              <section>
                <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">
                  Overlay trace
                </h3>
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                  {Object.entries(payload.overlay_trace).map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs text-slate-500 uppercase">
                        {k.replace(/_/g, ' ')}
                      </dt>
                      <dd className="text-slate-300 font-mono text-xs mt-0.5">
                        {v == null
                          ? '—'
                          : typeof v === 'object'
                          ? JSON.stringify(v)
                          : String(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {/* Data quality */}
            {payload.data_quality && (
              <section>
                <h3 className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">
                  Data quality
                </h3>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {Object.entries(payload.data_quality).map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-xs text-slate-500 uppercase">
                        {k.replace(/_/g, ' ')}
                      </dt>
                      <dd className="font-mono text-slate-300 mt-0.5">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
                {payload.history_sample_size != null && (
                  <p className="text-xs text-slate-500 mt-3">
                    Z-score history sample size: {payload.history_sample_size}
                  </p>
                )}
              </section>
            )}

            {payload.commentary && (
              <section>
                <h3 className="text-sm font-semibold text-slate-300 mb-2 uppercase tracking-wide">
                  Commentary
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">{payload.commentary}</p>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
